<script>
    import { onMount, onDestroy } from "svelte";
    import { selectedItem } from "../../store/store.js";
    import { fetchBrf, slugify } from "../../utils/utils.js";
    import Rating from "../../components/Rating/Rating.svelte";
    import RatingTraditional from "../../components/Rating/RatingTraditional.svelte";
    import Header from "../../components/Header.svelte";
    import Logotype from "../../components/Logotype.svelte";
    import Search from "../../components/Search.svelte";
    import { Link } from "svelte-routing";

    export let id;
    let loading = true;
    let error = null;
    let viewMode = "normal"; // 'ai' or 'normal'

    // SEO Variables
    let pageTitle = "Betygsrapport | Boanalys";
    let metaDescription =
        "Se detaljerad betygsrapport och analys för din bostadsrättsförening.";
    let canonicalUrl = "https://boanalys.io/";
    let jsonLdData = null;

    onMount(async () => {
        document.body.classList.add("report-page");
        try {
            loading = true;
            await fetchBrf(id);

            if ($selectedItem) {
                pageTitle = `Rapport: ${$selectedItem.brf_name} | Boanalys`;
                metaDescription = `Djupgående analys och betyg för ${$selectedItem.brf_name}. Se föreningens ekonomiska hälsa, energistatus och mer.`;
                const slug = slugify($selectedItem.brf_name);
                canonicalUrl = `https://boanalys.io/brf/${id}/${slug}/report`;

                // Structured Data for Report/Article
                jsonLdData = {
                    "@context": "https://schema.org",
                    "@type": "Report",
                    headline: `Analysrapport för ${$selectedItem.brf_name}`,
                    about: {
                        "@type": "RealEstateOrganization",
                        name: $selectedItem.brf_name,
                    },
                    author: {
                        "@type": "Organization",
                        name: "Boanalys",
                    },
                    datePublished: new Date().toISOString().split("T")[0], // Or use a relevant date from data if available
                    mainEntityOfPage: {
                        "@type": "WebPage",
                        "@id": canonicalUrl,
                    },
                };
            }
        } catch (err) {
            console.error("Error loading BRF data for report:", err);
            error = "Kunde inte ladda betygsrapport.";
        } finally {
            loading = false;
        }
    });

    onDestroy(() => {
        document.body.classList.remove("report-page");
    });
</script>

<svelte:head>
    <title>{pageTitle}</title>
    <meta name="description" content={metaDescription} />
    <link rel="canonical" href={canonicalUrl} />

    <!-- Open Graph -->
    <meta property="og:type" content="article" />
    <meta property="og:url" content={canonicalUrl} />
    <meta property="og:title" content={pageTitle} />
    <meta property="og:description" content={metaDescription} />
    <meta
        property="og:image"
        content="https://boanalys.io/bo-emblem-black.png"
    />

    {#if jsonLdData}
        {@html `<script type="application/ld+json">${JSON.stringify(jsonLdData)}</script>`}
    {/if}
</svelte:head>

<main>
    {#if loading}
        <div
            class="loading-container"
            style="width: 250px; margin-right: auto; margin-left: auto; margin-bottom: 80px; margin-top: 80px;"
        >
            <div class="about-text">
                <p>Laddar rapport...</p>
                <div style="height: 60px;margin-bottom: 30px;" class="question">
                    <Logotype />
                </div>
            </div>
        </div>
    {:else if error}
        <div class="error-container">
            <p>{error}</p>
        </div>
    {:else if $selectedItem}
        <div class="report-container">
            <div class="view-toggle">
                <Link to={`/brf/${id}/${slugify($selectedItem.brf_name)}`}>
                    &larr; Tillbaka
                </Link>
            </div>

            <div class="view-toggle">
                <button
                    class:active={viewMode === "normal"}
                    on:click={() => (viewMode = "normal")}
                    >Nyckeltalsanalys</button
                >
                <button
                    class:active={viewMode === "ai"}
                    on:click={() => (viewMode = "ai")}>AI-Betyg</button
                >
            </div>

            {#if viewMode === "ai"}
                <div style="padding: 0 20px;">
                    <Rating showDetails={true} />
                </div>
            {:else}
                <div class="rating-traditional-wrapper">
                    <RatingTraditional />
                </div>
            {/if}
        </div>
    {:else}
        <div class="error-container">
            <p>Kunde inte hitta information för att visa rapporten.</p>
        </div>
    {/if}
</main>

<style>
    :global(body.report-page) {
        background: linear-gradient(
                135deg,
                var(--primary-color) 0%,
                #e6eaf2 100%
            ),
            radial-gradient(
                circle at 20% 50%,
                rgba(120, 119, 198, 0.1) 0%,
                transparent 50%
            ),
            radial-gradient(
                circle at 80% 80%,
                rgba(255, 255, 255, 0.1) 0%,
                transparent 50%
            );
        background-attachment: fixed;
        background-size:
            100% 100%,
            100% 100%,
            100% 100%;
        background-repeat: no-repeat;
        min-height: 100vh;
    }

    main {
        max-width: 1200px;
        margin: 0 auto;
    }

    .rating-traditional-wrapper {
        position: relative;
        margin-left: auto;
        margin-right: auto;
        width: 100%;
        max-width: 100%;
    }

    .report-container {
        margin-top: 2rem;
    }
    .loading-container,
    .error-container {
        text-align: center;
        margin-top: 50px;
    }

    .view-toggle {
        display: flex;
        justify-content: center;
        gap: 0.5rem;
        background: linear-gradient(
            135deg,
            rgba(255, 255, 255, 0.1) 0%,
            rgba(255, 255, 255, 0.05) 100%
        );
        backdrop-filter: blur(30px) saturate(120%);
        -webkit-backdrop-filter: blur(30px) saturate(120%);
        border: none;
        border-radius: 16px;
        padding: 0.5rem;
        width: fit-content;
        margin-left: auto;
        margin-right: auto;
        margin-bottom: 1rem;
        box-shadow:
            0 8px 32px rgba(0, 0, 0, 0.06),
            inset 0 1px 0 rgba(255, 255, 255, 0.3),
            inset 0 -1px 0 rgba(255, 255, 255, 0.1),
            0 0 0 1px rgba(255, 255, 255, 0.15);
    }

    .view-toggle :global(a) {
        padding: 10px 20px;
        background: rgba(255, 255, 255, 0.2);
        backdrop-filter: blur(15px) saturate(120%);
        -webkit-backdrop-filter: blur(15px) saturate(120%);
        border: none;
        border-radius: 12px;
        color: #495057 !important;
        text-decoration: none !important;
        font-size: 0.95rem;
        font-weight: 500;
        transition: all 0.3s ease;
        box-shadow:
            0 2px 8px rgba(0, 0, 0, 0.05),
            0 0 0 1px rgba(255, 255, 255, 0.2);
    }

    .view-toggle :global(a:hover) {
        background: rgba(255, 255, 255, 0.35);
        color: #4361ee !important;
        transform: translateY(-2px);
        box-shadow:
            0 4px 12px rgba(0, 0, 0, 0.08),
            0 0 0 1px rgba(67, 97, 238, 0.3);
    }

    .view-toggle button {
        padding: 10px 20px;
        border: none;
        background: rgba(255, 255, 255, 0.2);
        backdrop-filter: blur(15px) saturate(120%);
        -webkit-backdrop-filter: blur(15px) saturate(120%);
        cursor: pointer;
        font-size: 1rem;
        font-weight: 500;
        color: #495057;
        border-radius: 12px;
        transition: all 0.3s ease;
        box-shadow:
            0 2px 8px rgba(0, 0, 0, 0.05),
            0 0 0 1px rgba(255, 255, 255, 0.2);
    }

    .view-toggle button:hover {
        background: rgba(255, 255, 255, 0.35);
        color: #4361ee;
        transform: translateY(-2px);
        box-shadow:
            0 4px 12px rgba(0, 0, 0, 0.08),
            0 0 0 1px rgba(67, 97, 238, 0.3);
    }

    .view-toggle button.active {
        background: rgba(255, 255, 255, 0.3);
        color: #4361ee;
        box-shadow:
            0 4px 12px rgba(67, 97, 238, 0.15),
            inset 0 1px 0 rgba(255, 255, 255, 0.4),
            0 0 0 1px rgba(67, 97, 238, 0.25);
    }

    @media (max-width: 576px) {
        .view-toggle {
            flex-direction: column;
            gap: 0.5rem;
            padding: 0.75rem;
            border-radius: 16px;
        }

        .view-toggle :global(a),
        .view-toggle button {
            padding: 12px 20px;
        }
    }
</style>
